import { Component, type ErrorInfo, type ReactNode } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { db, firebaseReady } from '../lib/firebase';

interface State {
  error: Error | null;
}

/**
 * Last line of defense: if anything crashes during render, show a friendly
 * recovery screen (with the actual error message for debugging) instead of
 * React's default silent blank page. Crashes are also reported to the
 * `crashes` Firestore collection so they can be diagnosed with sourcemaps.
 */
export default class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('App crashed:', error, info.componentStack);
    if (firebaseReady) {
      addDoc(collection(db(), 'crashes'), {
        createdAt: Date.now(),
        message: String(error?.message ?? error).slice(0, 480),
        stack: String(error?.stack ?? '').slice(0, 3800),
        componentStack: String(info?.componentStack ?? '').slice(0, 1800),
        path: window.location.pathname + window.location.search,
        userAgent: navigator.userAgent.slice(0, 300),
      }).catch(() => {});
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-night-950 p-6 text-center">
        <h1 className="font-display text-2xl font-bold text-white">Well, that wasn't supposed to happen.</h1>
        <p className="mt-3 max-w-md text-sm text-zinc-400">
          Something went wrong loading this page. Reloading usually fixes it — if it keeps
          happening, let us know and mention the message below.
        </p>
        <code className="mt-4 max-w-lg overflow-x-auto rounded-lg border border-white/10 bg-white/[0.03] px-4 py-2 text-xs text-amber-300">
          {this.state.error.message}
        </code>
        <button
          className="mt-6 rounded-xl bg-gradient-to-r from-sky-600 to-sky-400 px-6 py-3 font-semibold text-white"
          onClick={() => window.location.reload()}
        >
          Reload Page
        </button>
      </div>
    );
  }
}
